<script setup lang="ts">
import type { CmsElementForm } from "@shopware/composables";
import type { RequestParameters } from "#shopware";
import { ApiClientError } from "@shopware/api-client";

const props = defineProps<{
    content?: CmsElementForm
}>()

// Composables
const { t } = useI18n()
const { recaptcha } = useRecaptchaV3()
const { getSalutations } = useSalutations()
const {
    newsletterSubscribe,
    newsletterUnsubscribe,
} = useNewsletter()
const { resolveApiErrors } = useApiErrorsResolver()
const { error, success } = useGlobalNotifications()

// State
const isLoading = ref(false)
const formData = reactive<RequestParameters<'newsletterSubscribe'> & {
    birthdayYear?: number,
    birthdayMonth?: number,
    birthdayDay?: number,
}>({
    option: 'subscribe',
    salutationId: '',
    firstName: '',
    lastName: '',
    email: '',
    acceptedDataProtection: false,
    birthdayYear: undefined,
    birthdayMonth: undefined,
    birthdayDay: undefined,
})

// Computed
const title = computed(() => {
    return props.content?.config?.title?.value ?? t('newsletter.headline')
})

const newsletterActionOptions = computed(() => [
    { value: 'subscribe', label: t('newsletter.subscribeOption') },
    { value: 'unsubscribe', label: t('newsletter.unsubscribeOption') }
])

const salutationOptions = computed(() =>
    getSalutations.value.map(salutation => ({
        value: salutation.id,
        label: salutation.displayName
    }))
)

const isFormValid = computed(() => {
    const requiredFields: string[] = [
        formData.option,
        ...(formData.option === 'subscribe'
            ? [formData.salutationId, formData.firstName, formData.lastName]
            : []),
        formData.email,
    ]

    const allFieldsFilled = requiredFields.every(field => field.trim().length > 0)
    const dataProtectionAccepted = formData.acceptedDataProtection

    return allFieldsFilled &&
        !isLoading.value &&
        dataProtectionAccepted
})

const handleSubmit = async () => {
    if (!isFormValid.value) return
    isLoading.value = true

    try {
        const isNotABot = await recaptcha()

        if (!isNotABot) {
            console.error('Recaptcha failed, please try again.')
            isLoading.value = false
            return
        }

        const { birthdayYear, birthdayMonth, birthdayDay, ...subscriptionData } = formData

        if (birthdayYear && birthdayMonth && birthdayDay) {
            subscriptionData.customFields = {
                newsletter_birthday: `${birthdayYear}-${String(birthdayMonth).padStart(2, '0')}-${String(birthdayDay).padStart(2, '0')}`
            }
        }

        if (subscriptionData.option === 'subscribe') {
            await newsletterSubscribe({
                ...subscriptionData,
            })
            success(t('newsletter.successSubscribed'))
        } else if (subscriptionData.option === 'unsubscribe') {
            await newsletterUnsubscribe(subscriptionData.email)
            success(t('newsletter.successUnsubscribed'))
        }
    } catch (apiError) {
        if (apiError instanceof ApiClientError) {
            const errorMessages = resolveApiErrors(apiError.details.errors)
            error(errorMessages.join(', ') || t('form.newsletter.unknownError'))
        } else {
            error(apiError instanceof Error ? apiError.message : t('form.newsletter.unknownError'))
        }
    } finally {
        isLoading.value = false
    }
}
</script>
<template>
    <div>
        <FoundationHeadline tag="h3" class="px-4 -ml-4 text-lg font-medium mb-2">
            {{ title }}
        </FoundationHeadline>

        <form @submit.prevent="handleSubmit">
            <div class="space-y-6">
                <div class="flex flex-col gap-4">
                    <!-- Newsletter Form Action -->
                    <div class="col-span-12">
                        <FoundationLabel for="newsletter-form-action" class="block" required>
                            {{ $t('newsletter.labelActionSelect') }}
                            <span aria-hidden="true">{{ $t('form.required') }}</span>
                        </FoundationLabel>
                        <FoundationSelect
                            id="newsletter-form-action"
                            v-model="formData.option"
                            :placeholder="$t('newsletter.labelActionSelect')"
                            :options="newsletterActionOptions"
                            required
                            :disabled="isLoading"
                            name="newsletterFormAction"
                            class="w-full"
                        />
                    </div>

                    <!-- Email -->
                    <ComponentTextInput
                        id="registration-email"
                        v-model="formData.email"
                        :label="$t('form.email')"
                        type="email"
                        :placeholder="$t('form.emailPlaceholder')"
                        :disabled="isLoading"
                        size="md"
                        bordered
                    />

                    <div v-if="formData.option === 'subscribe'" class="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <!-- Salutation -->
                        <div class="w-full">
                            <FoundationLabel for="newsletter-salutation" class="block sr-only" required>
                                {{ $t('form.salutation') }}
                                <span aria-hidden="true">{{ $t('form.required') }}</span>
                            </FoundationLabel>
                            <FoundationSelect
                                id="newsletter-salutation"
                                v-model="formData.salutationId"
                                :placeholder="$t('form.salutation')+$t('form.required')"
                                :options="salutationOptions"
                                :required="formData.option === 'subscription'"
                                :disabled="isLoading"
                                name="salutation"
                                class="w-full"
                            />
                        </div>

                        <!-- First Name -->
                        <ComponentTextInput
                            id="registration-first-name"
                            v-model="formData.firstName"
                            class="self-end"
                            :label="$t('form.firstName')+$t('form.required')"
                            type="text"
                            :placeholder="$t('form.firstNamePlaceholder')"
                            :required="formData.option === 'subscription'"
                            :disabled="isLoading"
                            size="md"
                            bordered
                        />

                        <!-- Last Name -->
                        <ComponentTextInput
                            id="registration-last-name"
                            v-model="formData.lastName"
                            class="self-end"
                            :label="$t('form.lastName')+$t('form.required')"
                            type="text"
                            :placeholder="$t('form.lastNamePlaceholder')"
                            :required="formData.option === 'subscription'"
                            :disabled="isLoading"
                            size="md"
                            bordered
                        />
                    </div>

                    <!-- Birth Date -->
                    <div class="grid grid-cols-3 gap-3">
                        <!-- Year -->
                        <ComponentTextInput
                            id="registration-year"
                            v-model.number="formData.birthdayYear"
                            :label="$t('form.birthdayYear')"
                            :show-label="false"
                            type="number"
                            :placeholder="$t('form.birthdayYear')"
                            :disabled="isLoading"
                            size="md"
                            bordered
                            :max="3000"
                        />

                        <!-- Month -->
                        <ComponentTextInput
                            id="registration-month"
                            v-model.number="formData.birthdayMonth"
                            :label="$t('form.birthdayMonth')"
                            :show-label="false"
                            type="number"
                            :placeholder="$t('form.birthdayMonth')"
                            :disabled="isLoading"
                            size="md"
                            bordered
                            :min="1"
                            :max="12"
                        />

                        <!-- Day -->
                        <ComponentTextInput
                            id="registration-day"
                            v-model.number="formData.birthdayDay"
                            :label="$t('form.birthdayDay')"
                            :show-label="false"
                            type="number"
                            :placeholder="$t('form.birthdayDay')"
                            :disabled="isLoading"
                            size="md"
                            bordered
                            :min="1"
                            :max="31"
                        />
                    </div>

                    <!-- Data Protection Checkbox -->
                    <div class="flex items-center">
                        <FoundationCheckbox
                            id="registration-data-protection"
                            v-model="formData.acceptedDataProtection"
                            :disabled="isLoading"
                            required
                        />
                        <FoundationLabel for="registration-data-protection" class="ml-2 text-sm" required>
                            {{ $t('newsletter.dataProtection') }}
                            <span aria-hidden="true">{{ $t('form.required') }}</span>
                        </FoundationLabel>
                    </div>

                    <p>
                        {{ $t('form.recaptchaHint') }}
                        <FoundationLink is="external" to="https://policies.google.com/privacy" target="_blank">
                            {{ $t('form.recaptchaPrivatePolicy') }}
                        </FoundationLink>
                        {{ $t('form.recaptchaAnd') }}
                        <FoundationLink is="external" to="https://policies.google.com/terms" target="_blank">
                            {{ $t('form.recaptchaTac') }}
                        </FoundationLink>
                        {{ $t('form.recaptchaOf') }}
                    </p>

                    <!-- Submit Button -->
                    <div class="flex flex-col sm:flex-row gap-3 pt-4">
                        <FoundationButton
                            type="submit"
                            color="secondary"
                            class="flex-1"
                            :loading="isLoading"
                            :disabled="!isFormValid"
                            @click="handleSubmit"
                        >
                            {{ formData.option === 'subscribe' ? $t('newsletter.subscribeOption') : $t('newsletter.unsubscribeOption') }}
                        </FoundationButton>
                    </div>

                    <div class="text-sm">{{ $t('form.requiredFieldsInfo') }}</div>
                </div>
            </div>
        </form>
    </div>
</template>